import { Alert, Box, Button, Card, CardContent, Chip, LinearProgress, TextField, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, ImageIcon, UploadIcon } from 'lucide-react';
import React, { useState } from 'react';
import { useFeedbackApi } from '../services/api/useFeedback.api';
import { CreateGeneralFeedback } from '../types/Feedback/CreateGeneralFeedback.type';

const FeedbackPage = () => {
  const [feedback, setFeedback] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { sendFeedback } = useFeedbackApi();

  const mutation = useMutation({
    mutationFn: (data: CreateGeneralFeedback) => sendFeedback(data),
    onSuccess: () => {
      setFeedback('');
      setImage(null);
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
    },
    onError: (error) => {
      setError('An error occurred while submitting feedback: ' + error.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!feedback.trim()) {
      setError('Feedback is required');
      return;
    }

    mutation.mutate({ feedback, attachment: image });
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        setError('Invalid file type. Please upload PNG, JPG, JPEG, or GIF.');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must not exceed 5MB.');
        return;
      }
      setImage(file);
      setError(null);
    }
  };

  const removeImage = () => {
    setImage(null);
  };

  const { isSuccess, isPending } = mutation;

  return (
    <Box sx={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
      <Card elevation={3}>
        <CardContent sx={{ padding: 4 }}>
          <Typography
            variant='h5'
            gutterBottom
            align='center'
            color='primary'
            sx={{
              fontWeight: 700,
            }}
          >
            Submit Feedback
          </Typography>

          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isSuccess && (
            <Alert severity='success' icon={<CheckCircle fontSize='inherit' />} sx={{ mb: 2 }}>
              Feedback submitted successfully!
            </Alert>
          )}

          <Box component='form' onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              label='Your Feedback'
              multiline
              rows={6}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              margin='normal'
              required
              disabled={isPending}
              placeholder='Please share your thoughts, suggestions, or issues...'
              variant='filled'
            />

            <Box sx={{ my: 3 }}>
              <input
                accept='image/png,image/jpg,image/jpeg,image/gif'
                id='image-upload'
                type='file'
                multiple
                onChange={handleImageChange}
                style={{ display: 'none' }}
                disabled={isPending}
              />

              <label htmlFor='image-upload'>
                <Button
                  variant='outlined'
                  component='span'
                  startIcon={<UploadIcon size={16} />}
                  disabled={isPending}
                  sx={{ mr: 2 }}
                >
                  Attach Screenshot
                </Button>
              </label>

              {image && (
                <Chip
                  icon={<ImageIcon size={20} />}
                  label={image.name}
                  onDelete={removeImage}
                  variant='outlined'
                  sx={{ ml: 1, padding: 1 }}
                />
              )}

              <Typography
                variant='caption'
                sx={{
                  display: 'block',
                  mt: 1,
                  color: 'text.secondary',
                }}
              >
                Supported formats: PNG, JPG, JPEG, GIF. Max size: 5MB
              </Typography>
            </Box>

            {isPending && <LinearProgress sx={{ my: 2 }} />}

            <Button
              type='submit'
              variant='contained'
              color='primary'
              size='large'
              disabled={isPending || !feedback.trim()}
              fullWidth
              sx={{ mt: 3 }}
              disableElevation
            >
              {isPending ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default FeedbackPage;
